import React, {useEffect, useState} from "react";
import RequestCard from "../../widget/RequestCard";
import {useTranslation} from "react-i18next";
import {useParams, useNavigate} from "react-router-dom";
import * as yup from "yup";
import {useNotifications} from "common/src/hooks/notification/notificationHook";
import PromiseBuilder from "common/src/utils/PromiseUtils";
import ApiClient from "common/src/api/ApiClient";
import {useForm} from "common/src/hooks/form/formHook";
import OfferDetailsCard from "../../widget/OfferDetailsCard";
import Button from "../../../../../common/src/components/widget/Button";
import ClientCard from "../../widget/ClientCard";
import GuestCard from "../../widget/GuestsCard";
import {StringNotEmptyRegex} from "common/src/regex/StringNotEmptyRegex";
import TourPlanCard from "../../widget/TourPlanCard";
import TrailServiceCard from "../../widget/TrailServiceCard";
import CostBreakdownCard from "../../widget/CostBreakdownCard";
import {
  addDays,
  dateToString,
  daysBetweenDates, isTimeBetween, trimSeconds
} from "common/src/utils/DateUtils";
import moment from "moment";
import FormContainer from "common/src/components/form/FormContainer";
import {offerStatus} from "../../../model/offerStatus";


function OfferDetailsPage() {

  const [requestDetails, setRequestDetails] = useState(null);
  const [offerDetails, setOfferDetails] = useState(null);
  const {addErrorNotification, addSuccessNotification, addWarningNotification} = useNotifications();
  const {offerId} = useParams();
  const {requestId} = useParams();

  const {t} = useTranslation();
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const [requestIdOrig, setRequestIdOrig] = useState(requestId);


  function validateWorkHours(validationContext) {
    if(!validationContext.parent.service || !validationContext.parent.service.openingTime) {
      return true;
    }
    const value = validationContext.originalValue;
    const openingTime = trimSeconds(validationContext.parent.service.openingTime);
    const closingTime = trimSeconds(validationContext.parent.service.closingTime);

    return isTimeBetween(value, openingTime, closingTime) ?
      true : validationContext.createError({
        message: `${t('offer.service.time_during_work_hours')}` +
          `\n ${t('offer.service.opens')}: ${openingTime}` +
          `\n ${t('offer.service.closes')}: ${closingTime}`,
        path: validationContext.path
      });
  }

  const offerStatusList = offerStatus();
  const schema = {
    guest: yup.object().shape({
      name: yup.string().max(255, t('length_exceeded_error')).matches(StringNotEmptyRegex, t('first_name_format_error')).required(t('required_field')),
      surname: yup.string().max(255, t('length_exceeded_error')).matches(StringNotEmptyRegex, t('last_name_format_error')).required(t('required_field')),
      mobileNumber: yup.string().required(t('required_field')),
      email: yup.string().email(t('email_not_valid')).required(t('required_field')),
      birthDate: yup.date().max(new Date(), t('date_before_today')).nullable().notRequired(),
      isMainGuest: yup.boolean(),
    }),
    trailServicePrice: yup.number().min(0, t('greater_than_zero')),
    amountPaid: yup.number().typeError(t('required_field')).min(0, t('greater_than_zero')).required(t('required_field')),
    numberOfAdults: yup.number().min(1).max(2147483647, t('length_exceeded_error')).min(1, t('greater_than_zero')).required(t('required_field')).typeError(t('must_be_number')),
    numberOfChildren: yup.number().min(0).max(64, t('length_exceeded_error')).min(0, t('greater_than_zero')).required(t('required_field')).typeError(t('must_be_number')),

    startDate: yup.date().test('startDate', t('date_after_today_date'), function (value) {
      if(!value) {
        return true;
      }
      return (daysBetweenDates(moment(value), moment()) > 0);
    }),
    days: yup.array().of(
      yup.object().shape({
        services: yup.array().of(
          yup.object().shape({
            type: yup.string().required(t('required_field')),
            serviceName: yup.string().required(t('required_field')),
            service: yup.object().required(t('required_field')),
            price: yup.number().required(t('required_field')),
            pickUpTime: yup.string().when('type', {is: (type) => ['transport', 'bike_rental'].includes(type),
              then: yup.string().nullable().notRequired().test('custom-validation', 'Invalid pick up time', function (_, validationContext) {
                return validateWorkHours(validationContext);
              })}),
            returnTime: yup.string().when('type', {is: 'bike_rental',
              then: yup.string().nullable().notRequired().test('custom-validation', 'Invalid pick up time', function (_, validationContext) {
                return validateWorkHours(validationContext);
              })}),
            time: yup.string().when('type', {is: 'activity',
              then: yup.string().nullable().notRequired().test('custom-validation', 'Invalid pick up time', function (_, validationContext) {
                return validateWorkHours(validationContext);
              })}),
            numberOfGuests: yup.number().when('type', {is: (type) => ['accommodation', 'transport', 'activity'].includes(type),
              then: yup.number().required(t('required_field')).typeError(t('must_be_number'))
                .test('validNumberOfGuests', t('selected_service_allows_up_to_guests'), function (value) {
                if(this.parent.type === "accommodation" && this.parent.service && this.parent.service.numberOfGuests < value) {
                  const maxNumberOfGuests = this.parent.service.numberOfGuests
                  return this.createError({
                    message: t('selected_service_allows_up_to_guests', {maxNumberOfGuests}),
                    path: this.path,
                  });
                } else if(this.parent.type === "transport" && this.parent.service && this.parent.service.passengerCapacity < value) {
                  const maxNumberOfGuests = this.parent.service.passengerCapacity
                  return this.createError({
                    message: t('selected_service_allows_up_to_guests', {maxNumberOfGuests}),
                    path: this.path,
                  });
                }
                if((this.parent.type === "transport" && value < 0) || (this.parent.type !== "transport" && value <= 0)) {
                  return this.createError({
                    message: t('greater_than_zero'),
                    path: this.path,
                  });
                }
                return true;
            })}),
            details: yup.string().nullable(),
            quantity: yup.number().when('type', {is: 'bike_rental',
              then: yup.number().required(t('required_field'))}).test('maxBikes', t('max_number_of_bikes'), function (value, validationContext) {
              const service = validationContext.parent.service;
                if(!service || !service.quantityAvailable) {
                  return true;
                }
                if(value <= 0) {
                  return this.createError({
                    message: t('greater_than_zero'),
                    path: this.path,
                  });
                }
                if(value > service.quantityAvailable) {
                  return this.createError({
                    message: t('selected_service_allows_up_to_bikes', {maxNumberOfBikes: service.quantityAvailable}),
                    path: this.path,
                  });
                }
                return true;
            }),
            returnDate: yup.date().when('type', {is: 'bike_rental',
            then: yup.date().required(t('required_field')).test('startDate', t('return_date_after_day_date'), function (value) {
              if(!value) {
                return true;
              }
              return (daysBetweenDates(moment(value), moment(this.from[1].value.date)) >= 0);
            })}),
            fromLocation: yup.object().when('type', {is: 'transport',
              then : yup.object().shape({
                name: yup.string().required(t('required_field')),
                coordinates: yup.array().required(t('required_field'))
              })}),
            toLocation: yup.object().when('type', {is: 'transport',
              then : yup.object().shape({
                name: yup.string().required(t('required_field')),
                coordinates: yup.array().required(t('required_field'))
              })}),
            numberOfBikes: yup.number().when('type', {is: 'transport',
              then: yup.number().required(t('required_field')).typeError(t('required_field')).test('validNumberOfBikes', t('selected_service_allows_up_to_bikes'), function (value) {
                if(this.parent.service && this.parent.service.bikeCapacity < value) {
                  const maxNumberOfBikes = this.parent.service.bikeCapacity;
                  return this.createError({
                    message: t('selected_service_allows_up_to_bikes', {maxNumberOfBikes}),
                    path: this.path,
                  });
                }
                if(value < 0) {
                  return this.createError({
                    message: t('greater_or_equal_to_zero'),
                    path: this.path,
                  });
                }
                return true;
              })
            }),

          })
        )
      })
    )
  }

  const form = useForm(saveOffer, {}, schema, showFormWarning);
  const {
    values,
    setFormNotDirty,
    getField,
    handleSubmit,
    setValue,
    setValues,
    isEdited,
    triggerValidation,
  } = form;

  useEffect(() => {
    if (offerId && !requestIdOrig) {
      new PromiseBuilder()
        .then(async () => ApiClient.get(`/api/v1/booking/offer/${offerId}`))
        .then(async (res) => {
          const data = res.data.payload;
          setValues(data);
          setOfferDetails(data);
          setRequestIdOrig(data.requestId);
        })
        .catch((res) => {
          return addErrorNotification(t("toast.fetch_failed_code", {code: res.code}), t("toast.error"));
        })
        .run();
    }

    if (requestIdOrig) {
      fetchRequest();
    }

  }, [requestIdOrig,])

  function fetchRequest() {
    new PromiseBuilder()
      .then(async () => ApiClient.get(`/api/v1/booking/request/${requestIdOrig}`))
      .then(async (res) => {
        const data = res.data.payload;
        setRequestDetails(data);
        if(!values.guest){
          setValues(data);
          setValue("amountPaid", 0, undefined, null);
          setValue("days", [{
            stage: null,
            services: [{}]}
          ], undefined, null);
        }
      })
      .catch((res) => {
        return addErrorNotification(t("toast.fetch_failed_code", {code: res.code}), t("toast.error"));
      })
      .run();
  }

  function showFormWarning() {
    addWarningNotification(t('toast.fill_outlined_fields'), t('toast.info'))
  }

  function createRequestDataObject() {
    const startDate = getField("startDate").value;
    const endDate = dateToString(addDays(new Date(startDate), getField("days").value.length - 1), true);
    let stages = [];
    let services = {
      accommodations: [],
      activities: [],
      bikeRentals: [],
      transports: [],
    }
    const days = getField("days").value;
    days.map((day, dayIndex) => {
      const dayDate = day.date
      const nextDayDate = dateToString(addDays(new Date(dayDate), 1), true);
      day.services.map((element, serviceIndex) => {
        const service = day.services[serviceIndex];
        const serviceId = service.service.id;
        switch (service.type) {
          case 'accommodation': {
            const accommodationServiceDto = {
              id: service.id ?? undefined,
              accommodationServiceId: serviceId,
              price: service.price,
              checkInDate: dayDate,
              checkOutDate: nextDayDate,
              numberOfGuests: service.numberOfGuests,
              details: service.details
            }
            services.accommodations.push(accommodationServiceDto);
            break;
          }
          case 'bike_rental': {
            const bikeRentalServiceDto = {
              id: service.id ?? undefined,
              bikeRentalServiceId: serviceId,
              pickUpDate: dayDate,
              returnDate: service.returnDate,
              pickUpTime: service.pickUpTime,
              returnTime: service.returnTime,
              quantity: service.quantity,
              price: service.price,
              details: service.details
            }
            services.bikeRentals.push(bikeRentalServiceDto);
            break;
          }
          case 'transport': {
            const transportServiceDto = {
              id: service.id ?? undefined,
              transportServiceId: serviceId,
              pickUpDate: dayDate,
              pickUpTime: service.pickUpTime,
              fromLocation: service.fromLocation.name,
              coordinatesFrom: service.fromLocation.coordinates,
              toLocation: service.toLocation.name,
              coordinatesTo: service.toLocation.coordinates,
              price: service.price,
              numberOfPersons: service.numberOfGuests,
              numberOfBikes: service.numberOfBikes,
              details: service.details
            }
            services.transports.push(transportServiceDto);
            break;
          }
          case 'activity': {
            const activityServiceDto = {
              id: service.id ?? undefined,
              activityServiceId: serviceId,
              date: dayDate,
              time: service.time,
              price: service.price,
              numberOfPersons: service.numberOfGuests,
              details: service.details
            }
            services.activities.push(activityServiceDto);
            break;
          }
        }
      });
      day.stages?.map((stageId) => {
        const stage = {
          day: dayIndex + 1,
          stageId: stageId,
          offerId: offerId != null ? offerId : null,
        }
        stages.push(stage)
      })
    });
    let requestObject = {
      id: values.id,
      guest: values.guest,
      amountPaid: values.amountPaid,
      numberOfAdults: values.numberOfAdults,
      numberOfChildren: values.numberOfChildren,
      childrenAges: values.childrenAges ?? [],
      startDate: values.startDate,
      endDate: endDate,
      trailServicePrice: values.trailServicePrice !== undefined ? values.trailServicePrice : values.calculatedTrailServicePrice,
      ...services,
      stages: stages
    };
    if(offerId == null) {
      requestObject.requestId = requestObject.id;
      delete requestObject.id;
    }
    if(!('isMainGuest' in requestObject.guest)) {
      requestObject.guest.isMainGuest = true;
    }
    return requestObject;
  }

  /**
   *
   * @param values form values
   * @param postRequestAction function that will get executed after a successful save offer request
   */
  function saveOffer(values, postRequestAction) {
    let request = offerId ?
      async () => ApiClient.put(`/api/v1/booking/offer/${offerId}`, requestData) :
      async () => ApiClient.post(`/api/v1/booking/offer`, requestData);

    const requestData = createRequestDataObject();
    setIsLoading(true);

    new PromiseBuilder()
      .then((request))
      .then(() => {
        addSuccessNotification(offerId ? t("toast.edit_success") : t("toast.create_success"), t("toast.success"));
        setIsLoading(false);
        if(postRequestAction != null){
          postRequestAction();
        } else {
          setFormNotDirty();
        }
      }).then(() => {
        if(postRequestAction == null) {
          navigate('/offers');
        }
      })
      .catch((res) => {
        addErrorNotification(t("toast.fetch_failed_code", {code: res.code}), t("toast.error"))
        setIsLoading(false);
      })
      .run();
  }

  const rejectOffer = () => {
    let request = async () => ApiClient.put(`/api/v1/booking/offer/reject/${offerId}`);

    setIsLoading(true);

    new PromiseBuilder()
      .then((request))
      .then(() => {
        addSuccessNotification(t("toast.reject_success"));
        setFormNotDirty();
        setIsLoading(false);
      }).then(() => {
        navigate('/offers');
      })
      .catch(() => {
        addErrorNotification(t("toast.reject_error"))
        setIsLoading(false);
      })
      .run();
  }

  function sendOfferToClient() {
    let request = async () => ApiClient.put(`/api/v1/booking/offer/send/${offerId}`);

    setIsLoading(true);

    new PromiseBuilder()
      .then((request))
      .then(() => {
        addSuccessNotification(t("toast.send_success"));
        setFormNotDirty();
        setIsLoading(false);
      }).then(() => {
      navigate('/offers');
    })
      .catch(() => {
        addErrorNotification(t("toast.send_error"))
        setIsLoading(false);
      })
      .run();
  }

  function bookWithServiceProviders() {
    let request = async () => ApiClient.put(`/api/v1/booking/offer/book/${offerId}`);

    setIsLoading(true);

    new PromiseBuilder()
      .then((request))
      .then(() => {
        addSuccessNotification(t("toast.book_with_provider_success"));
        setFormNotDirty();
        setIsLoading(false);
      }).then(() => {
        navigate('/offers');
      })
      .catch(() => {
        addErrorNotification(t("toast.book_with_provide_error"))
        setIsLoading(false);
      })
      .run();
  }

  function bookingConfirmed() {
    let request = async () => ApiClient.put(`/api/v1/booking/offer/confirm/${offerId}`);

    setIsLoading(true);

    new PromiseBuilder()
      .then((request))
      .then(() => {
        addSuccessNotification(t("toast.booking_success"));
        setFormNotDirty();
        setIsLoading(false);
      }).then(() => {
        navigate('/offers');
      })
      .catch(() => {
        addErrorNotification(t("toast.booking_error"))
        setIsLoading(false);
      })
      .run();
  }

  function downloadTravelDocument(locale) {
    new PromiseBuilder().then(async () => ApiClient.getBinary(`/api/v1/document/travel/${locale}/${offerId}`))
      .then(async (res) => {
        const file = new Blob([res.data], {type: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'});
        let a = document.createElement("a"),
          url = URL.createObjectURL(file);
        a.href = url;
        a.download = "TravelDocument.docx";
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a)
      })
      .run();
  }

  function downloadOfferDocument(locale) {
    new PromiseBuilder().then(async () => ApiClient.getBinary(`/api/v1/document/offer/${locale}/${offerId}`))
      .then(async (res) => {
        const file = new Blob([res.data], {type: 'application/vnd.ms-excel'});
        let a = document.createElement("a"),
          url = URL.createObjectURL(file);
        a.href = url;
        a.download = "OfferDocument.xlsx";
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a)
      })
      .run();
  }

  function confirmBooking() {
    let isConfirmable = true;
    values.days.forEach(day => {
      day.services.forEach(service => {
        if(service.status !== 'CONFIRMED') {
          isConfirmable = false;
        }
      })
    })
    if(isConfirmable) {
      bookingConfirmed();
      return;
    }
    addWarningNotification(t('offer.booking_confirmed_warning'), t('warning'));
  }

  return (
    <div>
      <FormContainer onSubmit={handleSubmit} isEdited={isEdited}>
        <div className="columns is-widescreen mb-4">
          <div className="column is-9-widescreen">
            <RequestCard
              requestDetails={requestDetails}
            />
          </div>
          <div className=" column is-3-widescreen is-right">
            <OfferDetailsCard
              reject={rejectOffer}
              send={() => triggerValidation(undefined, () => saveOffer(values, sendOfferToClient))}
              bookWithProvider={() => triggerValidation(undefined, () => saveOffer(values, bookWithServiceProviders))}
              confirmed={confirmBooking}
              downloadDocumentEng={() => downloadTravelDocument("en")}
              downloadDocumentDe={() => downloadTravelDocument("de")}
              downloadOfferDocumentDe={() => downloadOfferDocument("de")}
              downloadOfferDocumentEng={() => downloadOfferDocument("en")}
              requestDetails={requestDetails}
              offerDetails={offerDetails}
              daysInOffer={getField('days').value?.length ?? 0}
              offerPrice={values.price ?? 0}
            />
          </div>
        </div>
        <div className="columns is-widescreen">
          <div className="column is-12-widescreen">
            <ClientCard
              dateOfBirth={getField("guest.birthDate")}
              name={getField("guest.name")}
              surname={getField("guest.surname")}
              mobileNumber={getField("guest.mobileNumber")}
              email={getField("guest.email")}
              mainGuest={getField("guest.isMainGuest")}
              isEditable={!(offerStatusList.map(status => status.id).filter(statusID => ['BOOKED', 'REJECTED'].includes(statusID)).includes(offerDetails?.status))}
            />
          </div>
        </div>
        <div className="columns is-widescreen">
          <div className="column is-12-widescreen">
            <GuestCard
              numberOfAdults={getField("numberOfAdults")}
              numberOfChildren={getField("numberOfChildren")}
              age={getField("childrenAges")}
              getField={getField}
              isEditable={!(offerStatusList.map(status => status.id).filter(statusID => ['BOOKED', 'REJECTED'].includes(statusID)).includes(offerDetails?.status))}
            />
          </div>
        </div>
        <div className="columns is-widescreen">
          <div className="column is-12-widescreen">
            <TourPlanCard
              form={form}
              offerDetails={offerDetails}
            />
          </div>
        </div>
        <div className="columns is-widescreen">
          <div className="column is-12-widescreen">
            <TrailServiceCard form={form}/>
          </div>
        </div>
        <div className="columns is-widescreen">
          <div className="column is-12-widescreen">
            <CostBreakdownCard form={form}/>
          </div>
        </div>
        <Button
          isSubmit={true}
          isLoading={isLoading}
          color="primary"
          label={offerId ? t('offer.save') : t('offer.create')}
        />
        <Button
          color="primary"
          label={t('offer.cancel')}
          isOutlined={true}
          onClick={() => {
            navigate("/offers");
          }}
        />
      </FormContainer>
    </div>
  );
}

export default OfferDetailsPage;
